import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fieldAPI, bookingAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  MapPin, Banknote, Clock, Calendar, ChevronLeft, ChevronRight, 
  Loader2, CheckCircle, XCircle, AlertCircle, Wallet 
} from 'lucide-react';

export default function BookField() {
  const { fieldId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [field, setField] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [slots, setSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (fieldId) {
      loadFieldData();
    }
  }, [fieldId, selectedDate]);

  const loadFieldData = async () => {
    setLoading(true);
    try {
      const response = await fieldAPI.getAvailability(fieldId, selectedDate);
      setField(response.data.field);
      setSlots(response.data.slots);
      setSelectedSlots([]);
    } catch (error) {
      toast.error('Fehler beim Laden der Verfügbarkeit');
    } finally {
      setLoading(false);
    }
  };

  const toggleSlot = (slotIndex) => {
    const slot = slots[slotIndex];
    if (slot.status !== 'available') return;
    
    setSelectedSlots(prev => {
      const exists = prev.includes(slotIndex);
      if (exists) {
        return prev.filter(i => i !== slotIndex);
      } else {
        // Check if consecutive
        const sorted = [...prev, slotIndex].sort((a, b) => a - b);
        let isConsecutive = true;
        for (let i = 1; i < sorted.length; i++) {
          if (sorted[i] - sorted[i-1] !== 1) {
            isConsecutive = false;
            break;
          }
        }
        if (!isConsecutive && prev.length > 0) {
          toast.error('Bitte wähle aufeinanderfolgende Zeitslots');
          return prev;
        }
        return sorted;
      }
    });
  };

  const calculateTotal = () => {
    if (selectedSlots.length === 0 || !field) return 0;
    return selectedSlots.length * field.price_per_hour;
  };

  const getTimeRange = () => {
    if (selectedSlots.length === 0) return null;
    const sorted = [...selectedSlots].sort((a, b) => a - b);
    const startSlot = slots[sorted[0]];
    const endSlot = slots[sorted[sorted.length - 1]];
    return {
      start: startSlot.start_time,
      end: endSlot.end_time
    };
  };

  const handleBook = async () => {
    if (selectedSlots.length === 0) {
      toast.error('Bitte wähle mindestens einen Zeitslot');
      return;
    }
    
    const total = calculateTotal();
    if (user.wallet_balance < total) {
      toast.error(`Nicht genügend Guthaben. Du brauchst ${total.toFixed(2)} TND`);
      return;
    }
    
    const timeRange = getTimeRange();
    
    setBooking(true);
    try {
      await bookingAPI.create({
        field_id: fieldId,
        date: selectedDate,
        start_time: timeRange.start,
        end_time: timeRange.end,
        notes: notes || undefined
      });
      
      toast.success('Buchung erstellt! Wartet auf Bestätigung des Feldbesitzers.');
      refreshUser();
      navigate('/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Buchung fehlgeschlagen');
    } finally {
      setBooking(false);
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    // Don't allow past dates
    const today = new Date().toISOString().split('T')[0];
    if (date.toISOString().split('T')[0] >= today) {
      setSelectedDate(date.toISOString().split('T')[0]);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  if (!field) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-muted-foreground">Platz nicht gefunden</p>
          <Button onClick={() => navigate('/fields')} className="mt-4">Zurück zur Übersicht</Button>
        </div>
      </Layout>
    );
  }

  const timeRange = getTimeRange();
  const total = calculateTotal();

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-4 px-4 sm:px-6">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2 -ml-2">
          <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
        </Button>

        {/* Field Info */}
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-tunis-blue">{field.name}</h1>
                <p className="text-muted-foreground flex items-center gap-1 mt-1 text-sm">
                  <MapPin className="w-4 h-4" /> {field.location}
                </p>
                {field.amenities?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {field.amenities.map((a, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{a}</Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-left sm:text-right">
                <p className="text-2xl sm:text-3xl font-bold text-tunis-teal">{field.price_per_hour} TND</p>
                <p className="text-sm text-muted-foreground">pro Stunde</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Datum wählen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-4">
              <Button variant="outline" size="icon" onClick={() => changeDate(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center">
                <Input
                  type="date"
                  value={selectedDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-[180px] text-center"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {new Date(selectedDate).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
              </div>
              <Button variant="outline" size="icon" onClick={() => changeDate(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Time Slots */}
        <Card>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Clock className="w-5 h-5" />
              Verfügbare Zeitslots
            </CardTitle>
            <p className="text-xs sm:text-sm text-muted-foreground">Tippe auf grüne Slots zum Auswählen</p>
          </CardHeader>
          <CardContent>
            {/* Legend */}
            <div className="flex flex-wrap gap-3 text-xs sm:text-sm mb-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-100 border border-green-300 rounded"></div>
                <span>Frei</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-tunis-teal rounded"></div>
                <span>Gewählt</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gray-200 border border-gray-300 rounded"></div>
                <span>Belegt</span>
              </div>
            </div>
            
            <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-8 gap-1.5 sm:gap-2">
              {slots.map((slot, index) => {
                const isSelected = selectedSlots.includes(index);
                const isAvailable = slot.status === 'available';
                
                let bgColor = 'bg-gray-200 border-gray-300 cursor-not-allowed opacity-50';
                if (isAvailable) {
                  bgColor = isSelected 
                    ? 'bg-tunis-teal text-white border-tunis-teal cursor-pointer active:scale-95' 
                    : 'bg-green-100 border-green-300 hover:bg-green-200 active:bg-green-300 cursor-pointer';
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => toggleSlot(index)}
                    disabled={!isAvailable}
                    className={`p-2 sm:p-3 rounded-lg border ${bgColor} transition-all touch-manipulation`}
                  >
                    <div className="text-center">
                      <span className="font-medium text-xs sm:text-sm">{slot.start_time}</span>
                      {isSelected && <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mx-auto mt-0.5" />}
                      {slot.status === 'booked' && <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mx-auto mt-0.5 text-gray-500" />}
                      {slot.status === 'blocked' && <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 mx-auto mt-0.5 text-gray-500" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Booking Summary */}
        {selectedSlots.length > 0 && (
          <Card className="border-tunis-teal">
            <CardHeader>
              <CardTitle>Buchungsübersicht</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Datum</p>
                  <p className="font-medium">{new Date(selectedDate).toLocaleDateString('de-DE')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Zeit</p>
                  <p className="font-medium">{timeRange?.start} - {timeRange?.end}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Dauer</p>
                  <p className="font-medium">{selectedSlots.length} Stunde(n)</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gesamtpreis</p>
                  <p className="text-2xl font-bold text-tunis-teal">{total.toFixed(2)} TND</p>
                </div>
              </div>
              
              <div>
                <Label>Notizen (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="z.B. Anzahl Spieler, besondere Wünsche..."
                  className="mt-1"
                />
              </div>
              
              {/* Wallet Info */}
              <div className={`p-3 rounded-lg ${user.wallet_balance >= total ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    <span>Dein Guthaben:</span>
                  </div>
                  <span className="font-bold">{user.wallet_balance?.toFixed(2)} TND</span>
                </div>
                {user.wallet_balance < total && (
                  <p className="text-sm text-red-600 mt-2">
                    Du brauchst noch {(total - user.wallet_balance).toFixed(2)} TND.
                    <Button variant="link" className="p-0 h-auto text-red-600 underline ml-1" onClick={() => navigate('/wallet')}>
                      Jetzt aufladen
                    </Button>
                  </p>
                )}
              </div>
              
              <Button
                className="w-full btn-primary h-12 text-lg"
                onClick={handleBook}
                disabled={booking || user.wallet_balance < total}
              >
                {booking ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  `Jetzt buchen für ${total.toFixed(2)} TND`
                )}
              </Button>
              
              <p className="text-xs text-center text-muted-foreground">
                Nach der Buchung muss der Feldbesitzer bestätigen. Bei Ablehnung wird der Betrag erstattet.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
